import React, { useState, useEffect } from "react";
import { Plus, Save, Check } from "lucide-react";
import SearchBar from "../common/SearchBar";
import FormModal from "../common/FormModal";
import ActionButtons, {
  PrimaryButton,
  SecondaryButton,
} from "../common/ActionButtons";
import { productsApi } from "../../utils/api";
import { PRODUCT_TYPES, BILLING_CYCLES, SERVICE_TYPES } from "../../constants";

// Explicit classes — dynamic `bg-${color}-100` strings get purged by Tailwind
const SERVICE_BADGE_CLASSES = {
  blue: "bg-blue-100 text-blue-800",
  purple: "bg-purple-100 text-purple-800",
  amber: "bg-amber-100 text-amber-800",
  emerald: "bg-emerald-100 text-emerald-800",
  teal: "bg-teal-100 text-teal-800",
  violet: "bg-violet-100 text-violet-800",
  cyan: "bg-cyan-100 text-cyan-800",
  indigo: "bg-indigo-100 text-indigo-800",
  rose: "bg-rose-100 text-rose-800",
  green: "bg-green-100 text-green-800",
  orange: "bg-orange-100 text-orange-800",
};

const ServiceBadge = ({ label, color }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full mr-1 mb-0.5 ${
      SERVICE_BADGE_CLASSES[color] || "bg-slate-100 text-slate-700"
    }`}
  >
    <Check className="w-3 h-3 mr-1" />
    {label}
  </span>
);

// products.included_services may arrive as a JS array (n8n) or a Postgres
// array literal string "{rag,slm}". Legacy rows fall back to the booleans.
const parseIncludedServices = (product) => {
  const raw = product.included_services ?? product.includedServices;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string" && raw.startsWith("{"))
    return raw.slice(1, -1).split(",").filter(Boolean);
  const legacy = [];
  if (product.includes_rag || product.includesRag) legacy.push("rag");
  if (product.includes_slm || product.includesSlm) legacy.push("slm");
  if (product.includes_private_llm || product.includesPrivateLlm)
    legacy.push("private_llm");
  return legacy;
};

const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingProduct, setEditingProduct] = useState(null);

  const [formData, setFormData] = useState({
    product_code: "",
    product_name: "",
    description: "",
    product_type: "rag",
    included_services: ["rag"],
    base_price: "",
    billing_cycle: "monthly",
    is_active: true,
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const data = await productsApi.getAll();
      setProducts(data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...formData,
        base_price: parseFloat(formData.base_price) || 0,
        // Legacy boolean flags kept in sync for older consumers
        includes_rag: formData.included_services.includes("rag"),
        includes_slm: formData.included_services.includes("slm"),
        includes_private_llm:
          formData.included_services.includes("private_llm"),
      };
      if (editingProduct) {
        await productsApi.update(editingProduct.id, payload);
      } else {
        await productsApi.create(payload);
      }
      fetchProducts();
      resetForm();
    } catch (error) {
      console.error("Error saving product:", error);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "Delete this product? This will affect any orders referencing it.",
      )
    )
      return;
    setLoading(true);
    try {
      await productsApi.delete(id);
      fetchProducts();
    } catch (error) {
      console.error("Error deleting product:", error);
    }
    setLoading(false);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      product_code: product.product_code || product.productCode || "",
      product_name: product.product_name || product.productName || "",
      description: product.description || "",
      product_type: product.product_type || product.productType || "rag",
      included_services: parseIncludedServices(product),
      base_price: product.base_price || product.basePrice || "",
      billing_cycle: product.billing_cycle || product.billingCycle || "monthly",
      is_active: product.is_active ?? product.isActive ?? true,
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      product_code: "",
      product_name: "",
      description: "",
      product_type: "rag",
      included_services: ["rag"],
      base_price: "",
      billing_cycle: "monthly",
      is_active: true,
    });
    setEditingProduct(null);
    setShowForm(false);
  };

  // Pre-select the matching service when product_type changes; combo starts
  // from RAG and the admin checks additional services.
  const handleProductTypeChange = (type) => {
    const preset =
      type === "combo"
        ? ["rag"]
        : SERVICE_TYPES.some((s) => s.value === type)
          ? [type]
          : [];
    setFormData({
      ...formData,
      product_type: type,
      included_services: preset,
    });
  };

  const toggleService = (value) => {
    setFormData((prev) => ({
      ...prev,
      included_services: prev.included_services.includes(value)
        ? prev.included_services.filter((s) => s !== value)
        : [...prev.included_services, value],
    }));
  };

  const filteredProducts = products.filter((product) => {
    const name = product.product_name || product.productName || "";
    const code = product.product_code || product.productCode || "";
    const type = product.product_type || product.productType || "";
    return (
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      type.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const getField = (product, ...keys) => {
    for (const key of keys) {
      if (product[key] !== undefined) return product[key];
    }
    return undefined;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          onRefresh={fetchProducts}
          placeholder="Search products..."
        />
        <PrimaryButton onClick={() => setShowForm(!showForm)} icon={Plus}>
          New Product
        </PrimaryButton>
      </div>

      <FormModal
        title={editingProduct ? "Edit Product" : "New Product"}
        isOpen={showForm}
        onClose={resetForm}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Product Code *
              </label>
              <input
                type="text"
                required
                value={formData.product_code}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    product_code: e.target.value.toUpperCase(),
                  })
                }
                placeholder="e.g. RAG, SLM, PRIVATE_LLM"
                disabled={!!editingProduct}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Product Type *
              </label>
              <select
                value={formData.product_type}
                onChange={(e) => handleProductTypeChange(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {PRODUCT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Product Name *
            </label>
            <input
              type="text"
              required
              value={formData.product_name}
              onChange={(e) =>
                setFormData({ ...formData, product_name: e.target.value })
              }
              placeholder="e.g. RAG Knowledge Base"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows={2}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Included services — drives facility_services provisioning on orders */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Included Services
            </label>
            <div className="grid grid-cols-2 gap-2 bg-slate-50 p-3 rounded-lg">
              {SERVICE_TYPES.map((svc) => (
                <label
                  key={svc.value}
                  className="flex items-center space-x-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={formData.included_services.includes(svc.value)}
                    onChange={() => toggleService(svc.value)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
                  />
                  <span className="text-sm text-slate-700">{svc.label}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-1">
              Provisioning an order for this product creates these services on
              the facility.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Base Price ($)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.base_price}
                onChange={(e) =>
                  setFormData({ ...formData, base_price: e.target.value })
                }
                placeholder="499.00"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Billing Cycle
              </label>
              <select
                value={formData.billing_cycle}
                onChange={(e) =>
                  setFormData({ ...formData, billing_cycle: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {BILLING_CYCLES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData({ ...formData, is_active: e.target.checked })
                  }
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
                />
                <span className="text-sm text-slate-700">Active</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <SecondaryButton onClick={resetForm}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" disabled={loading} icon={Save}>
              {loading ? "Saving..." : "Save Product"}
            </PrimaryButton>
          </div>
        </form>
      </FormModal>

      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Code
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Product
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Services
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Price
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Billing
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {loading ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-6 py-4 text-center text-slate-500"
                >
                  Loading...
                </td>
              </tr>
            ) : filteredProducts.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-6 py-4 text-center text-slate-500"
                >
                  No products found. Products are seeded from the database
                  migration.
                </td>
              </tr>
            ) : (
              filteredProducts.map((product) => {
                const isActive =
                  getField(product, "is_active", "isActive") ?? true;
                const code =
                  getField(product, "product_code", "productCode") || "-";
                const name =
                  getField(product, "product_name", "productName") || "-";
                const type =
                  getField(product, "product_type", "productType") || "-";
                const price = getField(product, "base_price", "basePrice");
                const billing =
                  getField(product, "billing_cycle", "billingCycle") ||
                  "monthly";
                const services = parseIncludedServices(product);

                return (
                  <tr key={product.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono font-medium text-slate-900">
                      {code}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-900">
                      <div className="font-medium">{name}</div>
                      {product.description && (
                        <div className="text-xs text-slate-500 mt-0.5 max-w-xs truncate">
                          {product.description}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 capitalize">
                      {type}
                    </td>
                    <td className="px-6 py-4 text-sm max-w-xs">
                      {services.length === 0 ? (
                        <span className="text-xs text-slate-400">
                          No services
                        </span>
                      ) : (
                        services.map((s) => {
                          const meta = SERVICE_TYPES.find((t) => t.value === s);
                          return (
                            <ServiceBadge
                              key={s}
                              label={meta?.label || s}
                              color={meta?.color}
                            />
                          );
                        })
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {price ? `$${parseFloat(price).toFixed(2)}` : "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 capitalize">
                      {billing}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          isActive
                            ? "bg-green-100 text-green-800"
                            : "bg-slate-100 text-slate-800"
                        }`}
                      >
                        {isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      <ActionButtons
                        onEdit={() => handleEdit(product)}
                        onDelete={() => handleDelete(product.id)}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductManagement;
